import React from "react";
import { Link } from "react-router-dom";
import PageShell from "../components/PageShell";
import PortfolioSection from "../components/PortfolioSection";
import MarketingIntakeForm from "../components/MarketingIntakeForm";
import MarketingPackages from "../components/MarketingPackages";
import { ArrowRight, Video, Globe, Smartphone, MousePointerClick, Camera, Users, Heart, PlayCircle } from "lucide-react";

const videoThemes = [
  { icon: Camera, kicker: "Product spotlight", title: "Show what you sell, fast", desc: "A 15-second product video that stops the scroll and makes the sale make sense." },
  { icon: Users, kicker: "Behind the scenes", title: "Let people meet the people", desc: "The faces behind the business — the single easiest way to build real trust online." },
  { icon: Heart, kicker: "Customer story", title: "Let happy customers speak", desc: "Short testimonials from the people you've already helped. Nothing sells like that." },
];

const steps = [
  { n: 1, title: "Tell us about you", desc: "Who you serve, what you sell, and where you want to be." },
  { n: 2, title: "We make a plan", desc: "Video ideas, and the pages your website needs." },
  { n: 3, title: "We create", desc: "You review everything before it goes live." },
  { n: 4, title: "You grow", desc: "We keep the content coming and improve as we learn." },
];

export default function MarketingWebsites() {
  return (
    <PageShell
      kicker="Marketing & Websites"
      title="Get your business seen, followed and remembered."
      subtitle="We create the videos that grow your social media and build the website that turns new followers into customers."
    >
      {/* Hero CTA buttons under hero */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-4 pb-8 text-center">
        <div className="flex flex-wrap gap-3 justify-center">
          <a href="#intake" className="btn-peach" data-testid="mw-free-analysis-cta">Free analysis <ArrowRight className="w-4 h-4" /></a>
          <a href="#packages" className="btn-ghost-navy" data-testid="mw-packages-cta">See packages <ArrowRight className="w-4 h-4" /></a>
          <a href="#services" className="btn-ghost-navy" data-testid="mw-services-cta">What we do <ArrowRight className="w-4 h-4" /></a>
        </div>
      </section>

      {/* Hero visual band */}
      <section className="max-w-6xl mx-auto px-5 lg:px-8 pb-16">
        <div className="relative rounded-3xl overflow-hidden bg-[color:var(--dms-navy)]">
          <img
            src="https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?auto=format&fit=crop&w=1600&q=80"
            alt="Short-form video shoot for small business"
            className="w-full h-[380px] md:h-[520px] object-cover opacity-80"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--dms-navy)]/90 via-[color:var(--dms-navy)]/30 to-transparent" />
          <div className="absolute left-0 right-0 bottom-0 p-6 md:p-10 text-white">
            <p className="kicker text-[color:var(--dms-peach)]">Your story, on camera</p>
            <h2 className="font-serif text-3xl md:text-5xl mt-3 max-w-2xl leading-tight">Short-form video for your brand</h2>
          </div>
        </div>
      </section>

      {/* Two ways we help */}
      <section id="services" className="max-w-7xl mx-auto px-5 lg:px-8 py-16 scroll-mt-24">
        <div className="text-center">
          <p className="kicker">What we do</p>
          <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--dms-navy)] mt-4">Two ways we help you grow</h2>
          <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
          <p className="mt-6 text-[color:var(--dms-navy)]/70 max-w-2xl mx-auto">Most businesses don't need more posts. They need people to stop scrolling, understand what they do, and know where to go next. That's what we build.</p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-8 hover-lift" data-testid="mw-service-video">
            <div className="w-12 h-12 rounded-full bg-[color:var(--dms-peach-light)]/60 flex items-center justify-center">
              <Video className="w-5 h-5 text-[color:var(--dms-navy)]" />
            </div>
            <h3 className="font-serif text-2xl text-[color:var(--dms-navy)] mt-5">Social media videos</h3>
            <p className="mt-3 text-[color:var(--dms-navy)]/70">We create videos made for your accounts, so your business shows up consistently and looks like it deserves attention.</p>
            <ul className="mt-6 space-y-2 text-sm text-[color:var(--dms-navy)]/75">
              <li className="flex gap-2"><span className="text-[color:var(--dms-peach)]">•</span> Video ideas built around your business and your customers</li>
              <li className="flex gap-2"><span className="text-[color:var(--dms-peach)]">•</span> Short videos made for Instagram, Facebook and TikTok</li>
              <li className="flex gap-2"><span className="text-[color:var(--dms-peach)]">•</span> A steady flow of content, so you're not starting from scratch every week</li>
            </ul>
          </div>

          <div className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-8 hover-lift" data-testid="mw-service-website">
            <div className="w-12 h-12 rounded-full bg-[color:var(--dms-peach-light)]/60 flex items-center justify-center">
              <Globe className="w-5 h-5 text-[color:var(--dms-navy)]" />
            </div>
            <h3 className="font-serif text-2xl text-[color:var(--dms-navy)] mt-5">Websites</h3>
            <p className="mt-3 text-[color:var(--dms-navy)]/70">We design and build websites that explain your business clearly and make it easy for people to contact you or buy.</p>
            <ul className="mt-6 space-y-2 text-sm text-[color:var(--dms-navy)]/75">
              <li className="flex gap-2"><span className="text-[color:var(--dms-peach)]">•</span> A clean design that matches your brand</li>
              <li className="flex gap-2"><span className="text-[color:var(--dms-peach)]">•</span> Works well on phones, where most visitors are</li>
              <li className="flex gap-2"><span className="text-[color:var(--dms-peach)]">•</span> Clear buttons that lead to a call, a message or a purchase</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Packages */}
      <MarketingPackages />

      {/* Video themes */}
      <section className="bg-[color:var(--dms-cream-2)]/50 py-20">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-center">
            <p className="kicker">Content</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--dms-navy)] mt-4">Videos that stop the scroll</h2>
            <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
            <p className="mt-6 text-[color:var(--dms-navy)]/70 max-w-2xl mx-auto">Short, vertical videos made for the way people watch on their phones. Here's the kind of content we make for businesses like yours.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {videoThemes.map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={i} className="relative rounded-2xl overflow-hidden bg-[color:var(--dms-navy)] aspect-[9/14] group" data-testid={`mw-video-theme-${i}`}>
                  <img
                    src={[
                      "https://images.unsplash.com/photo-1588345921523-c2dcdb7f1dcd?auto=format&fit=crop&w=800&q=80",
                      "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80",
                      "https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&w=800&q=80",
                    ][i]}
                    alt={v.title}
                    className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-80 transition-opacity"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--dms-navy)]/95 via-[color:var(--dms-navy)]/40 to-transparent" />
                  <div className="absolute top-5 right-5 w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                    <PlayCircle className="w-6 h-6 text-white" />
                  </div>
                  <div className="absolute left-0 right-0 bottom-0 p-6 text-white">
                    <div className="w-9 h-9 rounded-full bg-[color:var(--dms-peach)] flex items-center justify-center mb-4">
                      <Icon className="w-4 h-4 text-[color:var(--dms-navy)]" />
                    </div>
                    <p className="kicker text-[color:var(--dms-peach)]">{v.kicker}</p>
                    <h3 className="font-serif text-xl mt-2">{v.title}</h3>
                    <p className="mt-2 text-sm text-white/80">{v.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Website mockups */}
      <section className="max-w-6xl mx-auto px-5 lg:px-8 py-20">
        <div className="text-center">
          <p className="kicker">Websites</p>
          <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--dms-navy)] mt-4">Websites that make the next step obvious</h2>
          <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
          <p className="mt-6 text-[color:var(--dms-navy)]/70 max-w-2xl mx-auto">Clear pages, strong photos and buttons people can find. Built to look good on a phone first.</p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-10">
          <WebsiteCard
            label="Service business"
            domain="yourbusiness.com"
            tagline="Fresh cuts. Easy booking."
            cta="Book now"
            desc="Book a call or appointment in one tap."
            img="https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=900&q=80"
            icon={Smartphone}
          />
          <WebsiteCard
            label="Online store"
            domain="yourshop.com"
            tagline="Shop the collection"
            cta="Shop now"
            desc="Products, photos and a simple checkout path."
            img="https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=900&q=80"
            icon={MousePointerClick}
          />
        </div>
      </section>

      {/* Together band */}
      <section className="bg-[color:var(--dms-navy)] py-20">
        <div className="max-w-4xl mx-auto px-5 text-center">
          <p className="kicker text-[color:var(--dms-peach)]">Better together</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white mt-4">Videos and website, made to work together</h2>
          <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
          <p className="mt-6 text-white/80 max-w-2xl mx-auto">When one team handles both, your videos send people to a site that looks and sounds like the same business. Nobody lands on a page that feels like a different brand.</p>
          <div className="mt-8">
            <a href="#intake" className="btn-peach" data-testid="mw-tell-us-cta">Tell us about your business <ArrowRight className="w-4 h-4" /></a>
          </div>
          <p className="mt-10 font-serif italic text-white/60 text-lg">Every business has a story worth telling. We help you tell it where your customers are already looking.</p>
        </div>
      </section>

      {/* Portfolio */}
      <PortfolioSection />

      {/* Intake form */}
      <section id="intake" className="scroll-mt-24 bg-[color:var(--dms-cream)] py-20">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <MarketingIntakeForm />
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-5 lg:px-8 py-20">
        <div className="text-center">
          <p className="kicker">Process</p>
          <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--dms-navy)] mt-4">How it works</h2>
          <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-6">
          {steps.map((s) => (
            <div key={s.n} className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-6" data-testid={`mw-step-${s.n}`}>
              <div className="w-10 h-10 rounded-full bg-[color:var(--dms-peach)] flex items-center justify-center text-[color:var(--dms-navy)] font-serif text-lg font-semibold">{s.n}</div>
              <h3 className="font-serif text-xl text-[color:var(--dms-navy)] mt-4">{s.title}</h3>
              <p className="mt-2 text-sm text-[color:var(--dms-navy)]/65">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Apparel cross-sell */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 pb-20">
        <div className="rounded-3xl bg-[color:var(--dms-cream-2)]/70 border border-[color:var(--dms-navy)]/10 p-10 md:p-12 flex flex-col md:flex-row items-center gap-8 text-center md:text-left">
          <div className="flex-1">
            <p className="kicker">Also from DMS</p>
            <h3 className="font-serif text-3xl text-[color:var(--dms-navy)] mt-3">Need branded apparel too?</h3>
            <p className="mt-3 text-[color:var(--dms-navy)]/70">Design My Story started with T-shirts. Put your logo on polos, tees and hoodies for your team.</p>
          </div>
          <Link to="/t-shirts" className="btn-peach" data-testid="mw-shop-tshirts-cta">View more <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="bg-[color:var(--dms-navy)] py-20 scroll-mt-24">
        <div className="max-w-3xl mx-auto text-center px-5">
          <p className="kicker text-[color:var(--dms-peach)]">Contact</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white mt-4">Let's grow your business</h2>
          <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
          <p className="mt-6 text-white/80">Tell us what you sell and who you want to reach. We'll reply within one business day.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="btn-peach" data-testid="mw-send-message">Send us a message <ArrowRight className="w-4 h-4" /></Link>
            <a href="https://instagram.com/design.my.story" target="_blank" rel="noreferrer" className="btn-outline-light" data-testid="mw-instagram">@design.my.story <ArrowRight className="w-4 h-4" /></a>
          </div>
        </div>
      </section>
    </PageShell>
  );
}

function WebsiteCard({ label, domain, tagline, cta, desc, img, icon: Icon }) {
  return (
    <div className="group" data-testid={`mw-site-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="relative rounded-[32px] overflow-hidden bg-[color:var(--dms-navy)] border border-[color:var(--dms-navy)]/10 shadow-[0_30px_60px_-20px_rgba(20,36,61,0.35)]">
        <div className="px-4 pt-3 pb-2 bg-[color:var(--dms-navy)] text-white/70 text-[11px] flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-red-400" />
          <span className="w-2 h-2 rounded-full bg-yellow-400" />
          <span className="w-2 h-2 rounded-full bg-green-400" />
          <span className="mx-auto font-mono italic">{domain}</span>
        </div>
        <div className="relative aspect-[4/5] bg-[color:var(--dms-cream-2)] overflow-hidden">
          <img src={img} alt={tagline} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--dms-navy)]/85 via-transparent to-transparent" />
          <div className="absolute left-0 right-0 bottom-0 p-6 text-white">
            <p className="font-serif text-2xl md:text-3xl leading-tight max-w-xs">{tagline}</p>
            <button className="btn-peach mt-5">{cta} <ArrowRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
      <div className="mt-5 flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-[color:var(--dms-peach-light)]/60 flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-[color:var(--dms-navy)]" />
        </div>
        <div>
          <p className="font-serif text-lg text-[color:var(--dms-navy)]">{label}</p>
          <p className="text-sm text-[color:var(--dms-navy)]/65 mt-1">{desc}</p>
        </div>
      </div>
    </div>
  );
}
